// dom.ts — die paar Zeilen, die aus den React-Bausteinen Vanilla machen.
//
// Die sieben Bausteine waren zuerst React-Komponenten und haben ihr Markup als
// JSX beschrieben. Ohne React braucht es einen Ersatz, der genauso kurz ist —
// sonst wird aus jeder Komponente eine Wand aus createElement/appendChild.
//
// Bewusst KEIN innerHTML: der Inhalt kommt aus einem Textfeld im Designer, und
// den ungeprueft als HTML einzusetzen waere eine offene Tuer. Alles hier geht
// ueber createElement und Textknoten, damit bleibt Text auch Text.

/** Was als Kind erlaubt ist. `null`/`false` faellt weg — spart if-Kaskaden. */
export type Kind = Node | string | number | null | undefined | false;

type Attribute = Record<string, string | number | boolean | null | undefined>;

/**
 * Kurzform fuer createElement samt Attributen und Kindern.
 *
 *   el('td', { class: 'eoe-zelle' }, zeichneAus(text))
 *
 * Attribute mit `null`, `undefined` oder `false` werden weggelassen, nicht als
 * Text "false" gesetzt — genau wie es JSX vorher gemacht hat.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  name: K,
  attribute?: Attribute | null,
  ...kinder: Kind[]
): HTMLElementTagNameMap[K] {
  const knoten = document.createElement(name);

  if (attribute) {
    for (const [schluessel, wert] of Object.entries(attribute)) {
      if (wert === null || wert === undefined || wert === false) continue;
      knoten.setAttribute(schluessel, wert === true ? '' : String(wert));
    }
  }

  haenge(knoten, kinder);
  return knoten;
}

/** Dasselbe fuer SVG — dort braucht createElement einen Namensraum. */
export function svgEl(name: string, attribute?: Attribute | null, ...kinder: Kind[]): SVGElement {
  const knoten = document.createElementNS('http://www.w3.org/2000/svg', name);

  if (attribute) {
    for (const [schluessel, wert] of Object.entries(attribute)) {
      if (wert === null || wert === undefined || wert === false) continue;
      knoten.setAttribute(schluessel, wert === true ? '' : String(wert));
    }
  }

  haenge(knoten, kinder);
  return knoten;
}

/** Haengt Kinder an, flach — Arrays entstehen beim Mappen ueber Zeilen. */
export function haenge(ziel: Node, kinder: Kind[]): void {
  for (const kind of kinder) {
    if (kind === null || kind === undefined || kind === false) continue;
    ziel.appendChild(typeof kind === 'object' ? kind : document.createTextNode(String(kind)));
  }
}

/** Mehrere Knoten als ein Kind uebergeben. */
export function fragment(...kinder: Kind[]): DocumentFragment {
  const stueck = document.createDocumentFragment();
  haenge(stueck, kinder);
  return stueck;
}

/**
 * Text eines Knotens, mit `<br>` als Zeilenumbruch.
 *
 * `textContent` liesse `<br>` ersatzlos verschwinden — und genau so liefert
 * Webflow mehrzeilige Prop-Werte aus: die Umbrueche stehen im veroeffentlichten
 * HTML als `<br/>`. Ohne diese Uebersetzung kleben alle Zeilen zu einer
 * zusammen und aus drei Balken wird einer.
 */
function textMitUmbruechen(knoten: Node): string {
  let text = '';
  for (const kind of knoten.childNodes) {
    if (kind.nodeName === 'BR') text += '\n';
    else if (kind.nodeType === Node.TEXT_NODE) text += kind.textContent ?? '';
    else text += textMitUmbruechen(kind);
  }
  return text;
}

/**
 * Liest das Rohtext-Feld eines Bausteins.
 *
 * Kein `innerText`: das haengt vom Layout ab, und `[data-quelle]` ist per CSS
 * versteckt — dort waere es leer. Stattdessen `textContent` je Knoten, wobei
 * `<br>` (Webflows Form fuer Umbrueche in Prop-Werten) zu `\n` wird und
 * Blockelemente (`<p>` aus laengeren Designer-Eingaben) eigene Zeilen werden.
 */
export function lesQuelle(wurzel: Element): string {
  const quelle = wurzel.querySelector('[data-quelle]');
  if (!quelle) return '';

  // Ein <pre> traegt die Umbrueche im Text oder als <br> — beides deckt
  // textMitUmbruechen ab. Gleiches gilt fuer Quellen ohne Blockelemente.
  if (quelle.tagName === 'PRE' || !quelle.querySelector('p, div, li')) {
    return textMitUmbruechen(quelle);
  }

  const zeilen: string[] = [];
  for (const block of quelle.querySelectorAll('p, li, div')) {
    // Nur die unterste Ebene nehmen, sonst zaehlt verschachtelter Text doppelt.
    if (block.querySelector('p, li, div')) continue;
    zeilen.push(textMitUmbruechen(block).trim());
  }
  return zeilen.join('\n');
}

/** Liest ein `data-`Attribut als Wahrheitswert. Fehlt es, gilt `standard`. */
export function lesSchalter(wurzel: Element, name: string, standard: boolean): boolean {
  const wert = wurzel.getAttribute(name);
  if (wert === null || wert === '') return standard;
  return wert !== 'false' && wert !== '0' && wert !== 'aus' && wert !== 'nein';
}

/** Liest ein `data-`Attribut als Zahl und haelt sie in den Grenzen. */
export function lesZahl(
  wurzel: Element,
  name: string,
  standard: number,
  min: number,
  max: number,
): number {
  const wert = Number(wurzel.getAttribute(name));
  if (!Number.isFinite(wert) || wert === 0) return standard;
  return Math.min(Math.max(Math.round(wert), min), max);
}

/**
 * Der Platzhalter fuer ein leeres Textfeld.
 *
 * Ohne ihn stuende auf der Seite ein leerer Kasten, und im Designer waere nicht
 * zu sehen, ob der Baustein kaputt ist oder nur noch nicht befuellt.
 */
export function leerHinweis(text: string): HTMLElement {
  return el('p', { class: 'eoe-leer' }, text);
}
